import { X } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import { convertToLacCrore } from "@/app/utils/currency";
import { BuildingApartment, HouseLine } from "@phosphor-icons/react/dist/ssr";

export interface ISearchFilterTerms {
  locality?: string;
  minBedrooms?: number;
  hasServantRoom?: boolean;
  homeType?: Array<'apartment' | 'villa'>;
  minPrice?: number;
  maxPrice?: number;
  possession?: string;
  lat?: number; // Optional latitude for map/location search
  lon?: number; // Optional longitude for map/location search
  radius?: number; // Optional radius in meters for map/location search
  minArea?: number; // Minimum area in sqft
}

export interface ISearchFilterProps {
  terms?: ISearchFilterTerms;
  onFilterChange: (terms: ISearchFilterTerms) => void;
}

function SearchFilters({ terms, onFilterChange }: ISearchFilterProps) {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [newTerms, setNewTerms] = useState<ISearchFilterTerms>({ 
    minBedrooms: 0, 
    locality: '', 
    hasServantRoom: false, 
    homeType: ['apartment', 'villa'],
    maxPrice: 999999999,
    minPrice: 0
  });

  useEffect(() => {
    if (terms) {
      setNewTerms({
        ...terms,
        homeType: terms.homeType ?? ['apartment', 'villa'],
      });
    }
  }, [terms]);
  
  const onChange = (event: any) => {
    const { name, value } = event.target;
    let updatedTerms = {
      ...newTerms,
      [name]: Number(value),
    };
    // If minPrice is changed and becomes greater than maxPrice, adjust maxPrice
    if (name === 'minPrice' && Number(value) > (newTerms.maxPrice || 0)) {
      updatedTerms.maxPrice = Number(value) + 10000000;
    }
    // If maxPrice is changed and becomes less than minPrice, adjust minPrice
    if (name === 'maxPrice' && Number(value) < (newTerms.minPrice || 0)) {
      updatedTerms.minPrice = Number(value) - 10000000;
    }
    setNewTerms(updatedTerms);
  }
  
  // Helper function to format price display
  const formatPrice = (value: number) => {
    return convertToLacCrore(value, 0);
  };
  
  // Determine home type checkboxes
  const homeTypeArray = newTerms.homeType ?? ['apartment', 'villa'];
  const isApartmentChecked = homeTypeArray.includes('apartment');
  const isVillaChecked = homeTypeArray.includes('villa');

  // Hide filter on mobile after apply
  const handleApplyFilters = () => {
    onFilterChange(newTerms);
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsFilterVisible(false);
    }
  };

  return (
    <>
      {/* Floating Button (mobile only) */}
      {!isFilterVisible && (
        <button
          className="fixed bottom-6 left-6 z-50 md:hidden bg-white/90 hover:bg-primary/90 active:scale-95 shadow-2xl rounded-full p-4 flex items-center justify-center border-2 border-primary ring-2 ring-primary/30 transition-all duration-200 backdrop-blur-lg"
          onClick={() => setIsFilterVisible(true)}
          aria-label="Show Filters"
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 5h18M6 10h12M9 15h6" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}

      {/* Animated Backdrop (always mounted on mobile) */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${isFilterVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
        onClick={() => setIsFilterVisible(false)}
        aria-label="Close Filters Backdrop"
      />

      {/* Slide-in Filter Panel (always mounted on mobile) */}
      <aside
        className={`fixed top-0 left-0 h-full w-11/12 max-w-sm z-50 bg-white shadow-lg rounded-r-2xl overflow-y-auto transform transition-transform duration-300 md:static md:rounded-2xl md:shadow-lg md:w-full md:block
          ${isFilterVisible ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
        style={{ maxWidth: 400 }}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">Filters</h2>
          <button
            className="md:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setIsFilterVisible(false)}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Hide filters</span>
          </button>
        </div>
        <div className="flex flex-col gap-6 p-4">
          {/* Clipped locality field until further notice */}
          {/*
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Locality
            </label>
            <input
              type="text"
              value={newTerms.locality}
              className="w-full border border-gray-300 rounded-md shadow-xs p-2"
              placeholder="Enter locality"
              name="locality"
              onChange={onChange}
            />
          </div>
          */}
          <div>
            <label className="block text-base font-semibold text-gray-800 mb-2">
              Bedrooms
            </label>
            <select
              className="select select-bordered w-full text-base"
              value={newTerms.minBedrooms}
              name="minBedrooms"
              onChange={onChange}
            >
              <option value={0}>Any</option>
              <option value={1}>1+</option>
              <option value={2}>2+</option>
              <option value={3}>3+</option>
              <option value={4}>4+</option>
            </select>
          </div>
          <div>
            <label className="block text-base font-semibold text-gray-800 mb-2">
              Minimum Price {newTerms.minPrice && newTerms.minPrice > 0 ? `(₹${formatPrice(typeof newTerms.minPrice === 'number' ? newTerms.minPrice : 0)})` : ''}
            </label>
            <div className="space-y-2">
              <input
                type="range"
                min={0}
                max={50000000}
                step={10000000}
                value={typeof newTerms.minPrice === 'number' ? newTerms.minPrice : 0}
                className="range range-xs w-full"
                name="minPrice"
                onChange={onChange}
              />
              <div className="flex justify-between px-1 text-xs text-gray-500">
                {[0,1,2,3,4,5].map((n) => (
                  <span key={n} className="font-medium">{n}cr</span>
                ))}
              </div>
              <div className="text-center font-medium">
                
              </div>
            </div>
          </div>
          <div>
            <label className="block text-base font-semibold text-gray-800 mb-2">
              Maximum Price (₹{formatPrice(Number(newTerms.maxPrice || 100000000))})
            </label>
            <div className="space-y-2">
              <input
                type="range"
                min={10000000}  
                max={100000000}
                step={10000000}
                value={newTerms.maxPrice || 100000000}
                className="range range-xs w-full"
                name="maxPrice"
                onChange={onChange}
              />
              <div className="flex justify-between px-1 text-xs text-gray-500">
                {[1,3,5,7,9].map((n) => (
                  <span key={n} className="font-medium">{n}cr</span>
                ))}
              </div>
              <div className="text-center font-medium">
                
              </div>
            </div>
          </div>
          <div>
            <label className="block text-base font-semibold text-gray-800 mb-2">
              Property Type
            </label>
            <div className="flex flex-row gap-2">
              <label className={`flex items-center space-x-1 btn btn-outline btn-md w-1/2 ${isApartmentChecked ? 'btn-primary btn-active' : ''}`}>
                <BuildingApartment size={24} />
                <input
                  type="checkbox" 
                  className="checkbox checkbox-sm hidden"
                  checked={isApartmentChecked}
                  onChange={() => {
                    setNewTerms(prev => {
                      const current = prev.homeType ?? ['apartment', 'villa'];
                      let updated: Array<'apartment' | 'villa'>;
                      if (current.includes('apartment')) {
                        updated = current.filter(t => t !== 'apartment');
                      } else {
                        updated = [...current, 'apartment'];
                      }
                      // No default to both if empty
                      return { ...prev, homeType: updated };
                    });
                  }}
                />
                <span className="text-xs">Apartment</span>
              </label>
              <label className={`flex items-center space-x-1 btn btn-outline btn-md w-1/2 ${isVillaChecked ? 'btn-primary btn-active' : ''}`}>
                <HouseLine size={24} />
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm hidden"
                  checked={isVillaChecked}
                  onChange={() => {
                    setNewTerms(prev => {
                      const current = prev.homeType ?? ['apartment', 'villa'];
                      let updated: Array<'apartment' | 'villa'>;
                      if (current.includes('villa')) {
                        updated = current.filter(t => t !== 'villa');
                      } else {
                        updated = [...current, 'villa'];
                      }
                      // No default to both if empty
                      return { ...prev, homeType: updated };
                    });
                  }}
                />
                <span className="text-xs">Villa</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-base font-semibold text-gray-800 mb-2">
              Minimum Area {newTerms.minArea && newTerms.minArea > 0 ? `(${newTerms.minArea} sqft)` : ''}
            </label>
            <div className="space-y-2">
              <input
                type="range"
                min={800}
                max={6000}
                step={200}
                value={typeof newTerms.minArea === 'number' ? newTerms.minArea : 0}
                className="range range-xs w-full"
                name="minArea"
                onChange={onChange}
              />
              <div className="flex justify-between px-1 text-xs text-gray-500">
                {[0,3000,6000].map((n) => (
                  <span key={n} className="font-medium">{n} sqft</span>
                ))}
              </div>
              <div className="text-center font-medium">
                
              </div>
            </div>
          </div>
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maid Room
            </label>
          </div> */}
        </div>
        <div className="pt-2 px-4 pb-4">
          <button className="w-full btn btn-primary text-base font-semibold py-2 px-4 rounded-lg transition duration-300 flex items-center justify-center gap-2" onClick={handleApplyFilters} disabled={newTerms === terms}>
            Apply
          </button>
        </div>
      </aside>
    </>
  );
}

export default SearchFilters;
